import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { AnalyticsPeriodResult } from '../../services/analytics';

interface ComparisonBarChartProps {
  periods: AnalyticsPeriodResult[];
  topN?: number;
}

const PERIOD_COLORS = ['#6366f1', '#10b981', '#f59e0b'];

export default function ComparisonBarChart({ periods, topN = 8 }: ComparisonBarChartProps) {
  const { t, i18n } = useTranslation('search');
  const { translateCategoryName } = useCategoryTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  const data = useMemo(() => {
    // Sélectionne les top N catégories de dépenses (toutes périodes confondues, somme cumulée)
    const cumulative = new Map<string, { id: string; name: string; isDefault: boolean; cumul: number }>();
    periods.forEach((p) => {
      for (const cat of p.byCategory) {
        if (cat.type !== 'expense') continue;
        const existing = cumulative.get(cat.categoryId);
        if (existing) {
          existing.cumul += cat.total;
        } else {
          cumulative.set(cat.categoryId, {
            id: cat.categoryId,
            name: cat.name,
            isDefault: cat.isDefault,
            cumul: cat.total,
          });
        }
      }
    });

    const top = Array.from(cumulative.values())
      .sort((a, b) => b.cumul - a.cumul)
      .slice(0, topN);

    return top.map((cat) => {
      const row: Record<string, string | number> = {
        name: translateCategoryName(cat.id, cat.name, cat.isDefault),
      };
      periods.forEach((p, idx) => {
        const found = p.byCategory.find((c) => c.categoryId === cat.id);
        row[`period${idx}`] = found?.total ?? 0;
      });
      return row;
    });
  }, [periods, topN, translateCategoryName]);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 text-sm">
        {t('analytics.noChartData')}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('analytics.chartTitle')}</h3>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat(locale, { notation: 'compact', style: 'currency', currency: 'EUR' }).format(v)} />
          <Tooltip
            formatter={(v: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(v)}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          {periods.map((p, idx) => (
            <Bar
              key={idx}
              dataKey={`period${idx}`}
              name={p.label}
              fill={PERIOD_COLORS[idx % PERIOD_COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
